import { apiClient } from '@/lib/api'
import type {
  PaginatedStandardResponse,
  ErrorResponse,
  MessageResponse,
  PaginationInfo,
} from '@/types/api-response'
import {
  isErrorResponse,
  extractPaginatedData,
} from '@/types/api-response'
import type {
  BillingCycle,
  BillingCycleExpiryMismatch,
  ResourceWithoutBillingCycle,
  BillingCycleFilters,
  UpdateBillingCycleRequest,
} from '@/types/billing-cycle'

export const adminBillingCycleService = {
  /**
   * Get paginated list of billing cycles, optionally narrowed to a created-at range.
   * Returns: { data: BillingCycle[]; pagination: PaginationInfo }
   */
  async getBillingCycles(
    filters: BillingCycleFilters
  ): Promise<{ data: BillingCycle[]; pagination: PaginationInfo }> {
    // Parameter names must match GetBillingCyclesQuery's properties — the endpoint model-binds this
    // query string straight onto it, so anything misspelled is silently ignored rather than rejected.
    const params = new URLSearchParams()

    if (filters.createdFrom) params.append('createdFrom', filters.createdFrom)
    if (filters.createdTo) params.append('createdTo', filters.createdTo)
    if (filters.resourceId) params.append('resourceId', filters.resourceId)
    if (filters.search) params.append('search', filters.search)
    if (filters.tenantId) params.append('tenantId', filters.tenantId)
    if (filters.status) params.append('status', filters.status)
    if (filters.fundingSource) params.append('fundingSource', filters.fundingSource)
    params.append('pageNumber', filters.page.toString())
    params.append('pageSize', filters.pageSize.toString())
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)

    const response = await apiClient.get<PaginatedStandardResponse<BillingCycle> | ErrorResponse>(
      `/api/v1/admin/billing-cycles?${params.toString()}`
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractPaginatedData(response.data)
  },

  /**
   * Correct a billing cycle's window and status. SuperAdmin only — the API answers 403 for every
   * other admin role, so the caller should surface that verbatim rather than hide the button.
   */
  async updateBillingCycle(
    id: string,
    payload: UpdateBillingCycleRequest
  ): Promise<MessageResponse> {
    const response = await apiClient.put<MessageResponse | ErrorResponse>(
      `/api/v1/admin/billing-cycles/${id}`,
      payload
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return response.data
  },

  /**
   * Lists live machines that no active billing cycle accounts for — the sibling of
   * `getExpiryMismatches`, and the case that one cannot express: comparing an expiry against a
   * cycle end needs both, so a machine with no cycle never appears there.
   */
  async getResourcesWithoutCycle(
    page = 1,
    limit = 20,
  ): Promise<{ data: ResourceWithoutBillingCycle[]; pagination: PaginationInfo }> {
    const response = await apiClient.get<
      PaginatedStandardResponse<ResourceWithoutBillingCycle> | ErrorResponse
    >(`/api/v1/admin/billing-cycles/resources-without-cycle?page=${page}&limit=${limit}`);

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message);
    }

    return extractPaginatedData(response.data);
  },

  /**
   * Check which machines have an expiry that disagrees with their active billing cycle.
   * Read-only — the API reports, it never repairs.
   */
  async getExpiryMismatches(
    page = 1,
    limit = 20
  ): Promise<{ data: BillingCycleExpiryMismatch[]; pagination: PaginationInfo }> {
    const response = await apiClient.get<
      PaginatedStandardResponse<BillingCycleExpiryMismatch> | ErrorResponse
    >(`/api/v1/admin/billing-cycles/expiry-mismatches?page=${page}&limit=${limit}`)

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractPaginatedData(response.data)
  },
}
